/**
 * Support API client (staff surface, /admin/support). A different router from
 * the one the web app uses; this is the surface that carries internal notes.
 */

import { apiClient } from './client';
import type {
  AdminCreateSupportMessageRequest,
  AdminSupportSummary,
  AdminSupportTicketFilters,
  AdminSupportTicketsResponse,
  SupportTicketAdminDetail,
  UpdateSupportTicketRequest,
} from '@repo/types';

function toQuery(filters: AdminSupportTicketFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.priority) params.append('priority', filters.priority);
  if (filters.category) params.append('category', filters.category);
  if (filters.assignedTo) params.append('assignedTo', filters.assignedTo);
  if (filters.q) params.append('q', filters.q);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));

  const query = params.toString();
  return query ? `?${query}` : '';
}

class SupportApi {
  /** GET /api/admin/support/tickets */
  async getTickets(filters: AdminSupportTicketFilters = {}): Promise<AdminSupportTicketsResponse> {
    const response = await apiClient.get<AdminSupportTicketsResponse>(
      `/admin/support/tickets${toQuery(filters)}`
    );
    return response.data;
  }

  /** GET /api/admin/support/tickets/:id — full thread, internal notes included. */
  async getTicket(id: string): Promise<SupportTicketAdminDetail> {
    const response = await apiClient.get<SupportTicketAdminDetail>(`/admin/support/tickets/${id}`);
    return response.data;
  }

  /** POST /api/admin/support/tickets/:id/messages */
  async addMessage(
    id: string,
    payload: AdminCreateSupportMessageRequest
  ): Promise<SupportTicketAdminDetail> {
    const response = await apiClient.post<SupportTicketAdminDetail>(
      `/admin/support/tickets/${id}/messages`,
      payload
    );
    return response.data;
  }

  /** PATCH /api/admin/support/tickets/:id — status, priority, assignment. */
  async updateTicket(
    id: string,
    payload: UpdateSupportTicketRequest
  ): Promise<SupportTicketAdminDetail> {
    const response = await apiClient.patch<SupportTicketAdminDetail>(
      `/admin/support/tickets/${id}`,
      payload
    );
    return response.data;
  }

  /** DELETE /api/admin/support/tickets/:id — manager and admin only. */
  async deleteTicket(id: string): Promise<{ id: string }> {
    const response = await apiClient.delete<{ id: string }>(`/admin/support/tickets/${id}`);
    return response.data;
  }

  /** GET /api/admin/support/summary */
  async getSummary(): Promise<AdminSupportSummary> {
    const response = await apiClient.get<AdminSupportSummary>('/admin/support/summary');
    return response.data;
  }
}

export const supportApi = new SupportApi();
